import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/lib/utils"
import { Button } from "./button"
import { Card } from "./card"

// LIEC-specific Button variants
const liecButtonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap  text-sm font-semibold ring-offset-background transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        primary: "bg-liec-primary-500 text-white hover:bg-liec-primary-600 shadow-md hover:shadow-lg",
        secondary: "bg-liec-secondary-500 text-white hover:bg-liec-secondary-600 shadow-md hover:shadow-lg",
        outline: "border-2 border-liec-primary-500 text-liec-primary-500 hover:bg-liec-primary-500 hover:text-white",
        ghost: "text-liec-primary-500 hover:bg-liec-primary-50 hover:text-liec-primary-600",
        cta: "bg-gradient-to-r from-liec-primary-500 to-liec-primary-600 text-white hover:from-liec-primary-600 hover:to-liec-primary-700 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5",
      },
      size: {
        sm: "h-9 px-4 py-2 text-sm",
        md: "h-10 px-6 py-2.5 text-sm",
        lg: "h-12 px-8 py-3 text-base",
        xl: "h-14 px-10 py-4 text-lg",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "md",
    },
  }
)

export interface LiecButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof liecButtonVariants> {
  asChild?: boolean
}

export const LiecButton = React.forwardRef<HTMLButtonElement, LiecButtonProps>(
  ({ className, variant, size, ...props }, ref) => {
    return (
      <Button
        className={cn(liecButtonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
LiecButton.displayName = "LiecButton"

// LIEC-specific Card variants
const liecCardVariants = cva(
  " bg-white shadow-sm border transition-all duration-300",
  {
    variants: {
      variant: {
        default: "border-liec-neutral-200 hover:shadow-md",
        elevated: "border-liec-neutral-200 shadow-md hover:shadow-lg",
        interactive: "border-liec-neutral-200 hover:shadow-lg hover:border-liec-primary-200 cursor-pointer transform hover:-translate-y-1",
        service: "border-liec-neutral-200 hover:shadow-xl hover:border-liec-primary-300 cursor-pointer group overflow-hidden",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

export interface LiecCardProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof liecCardVariants> {}

export const LiecCard = React.forwardRef<HTMLDivElement, LiecCardProps>(
  ({ className, variant, ...props }, ref) => {
    return (
      <Card
        className={cn(liecCardVariants({ variant, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
LiecCard.displayName = "LiecCard"

// LIEC Section Container
export interface LiecSectionProps extends React.HTMLAttributes<HTMLElement> {
  as?: React.ElementType
  container?: boolean
}

export const LiecSection = React.forwardRef<HTMLElement, LiecSectionProps>(
  ({ className, as: Component = "section", container = true, children, ...props }, ref) => {
    const content = container ? (
      <div className="liec-container">{children}</div>
    ) : (
      children
    )

    return React.createElement(
      Component,
      {
        className: cn("liec-section", className),
        ref,
        ...props,
      },
      content
    )
  }
)
LiecSection.displayName = "LiecSection"

// LIEC Heading Components
export interface LiecHeadingProps extends React.HTMLAttributes<HTMLHeadingElement> {
  as?: "h1" | "h2" | "h3" | "h4" | "h5" | "h6"
  variant?: "primary" | "secondary" | "accent"
}

export const LiecHeading = React.forwardRef<HTMLHeadingElement, LiecHeadingProps>(
  ({ className, as: Component = "h2", variant = "primary", ...props }, ref) => {
    const variantClasses = {
      primary: "text-liec-neutral-900",
      secondary: "text-liec-neutral-700", 
      accent: "text-liec-primary-500",
    }

    return (
      <Component
        className={cn("liec-heading", variantClasses[variant], className)}
        ref={ref}
        {...props}
      />
    )
  }
)
LiecHeading.displayName = "LiecHeading"

export const LiecSubheading = React.forwardRef<HTMLHeadingElement, LiecHeadingProps>(
  ({ className, as: Component = "h3", variant = "secondary", ...props }, ref) => {
    const variantClasses = {
      primary: "text-liec-neutral-900",
      secondary: "text-liec-neutral-700",
      accent: "text-liec-primary-500",
    }

    return (
      <Component
        className={cn("liec-subheading", variantClasses[variant], className)}
        ref={ref}
        {...props}
      />
    )
  }
)
LiecSubheading.displayName = "LiecSubheading"

// LIEC Text Component
export interface LiecTextProps extends React.HTMLAttributes<HTMLParagraphElement> {
  variant?: "body" | "lead" | "small" | "muted"
}

export const LiecText = React.forwardRef<HTMLParagraphElement, LiecTextProps>(
  ({ className, variant = "body", ...props }, ref) => {
    const variantClasses = {
      body: "liec-text",
      lead: "text-lg md:text-xl text-liec-neutral-600 leading-relaxed",
      small: "text-sm text-liec-neutral-600",
      muted: "text-sm text-liec-neutral-500",
    }

    return (
      <p
        className={cn(variantClasses[variant], className)}
        ref={ref}
        {...props}
      />
    )
  }
)
LiecText.displayName = "LiecText"